import { getApiContext } from "shared/api";
import { AuthChannel, Delivery, DeliveryStatus } from "../types";
import { getWhiteLabelId } from "shared/api";

/**
 * Состояние входа v3. Хранится в localStorage, а не в sessionStorage:
 * в приложении пользователь уходит за кодом в Telegram/WhatsApp, и система
 * может выгрузить WebView вместе с sessionStorage. Срок жизни ограничен
 * сроком сессии (10 минут). OTP-код здесь не хранится.
 */
export type AuthSession = {
  phone: number;
  whiteLabelId?: number;
  apiContext: string;
  authSessionId: string;
  /** Срок сессии. */
  expiresAt: string;
  channels: AuthChannel[];
  /** Отправки этой сессии, от ранних к поздним. */
  deliveries: Delivery[];
  /** Раньше этого времени новая отправка запрещена. */
  nextSendAt?: string;
  /**
   * Отправка без ответа сервера (сетевая ошибка). Повтор того же канала
   * идёт с тем же request_id, чтобы сервер не отправил код дважды.
   */
  pendingRequest?: { channel: AuthChannel; requestId: string };
};

const STORAGE_KEY = "auth-v3-session";

const isExpired = (date?: string) => {
  const time = date ? Date.parse(date) : NaN;
  return Number.isNaN(time) || time <= Date.now();
};

/** Сессия совпадает с текущим брендом и окружением. */
const isSameContext = (session: AuthSession) =>
  session.whiteLabelId === getWhiteLabelId() &&
  session.apiContext === getApiContext();

export const loadAuthSession = (): AuthSession | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const session = JSON.parse(raw) as AuthSession;
    if (
      !session?.authSessionId ||
      isExpired(session.expiresAt) ||
      !isSameContext(session)
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return session;
  } catch {
    return null;
  }
};

export const saveAuthSession = (session: AuthSession) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Без хранилища вход работает, но не переживёт перезапуск.
  }
};

/**
 * Сохраняет результат запроса, только если вход ещё тот же: пока шёл запрос,
 * его могли завершить или сбросить — удалённую сессию не восстанавливаем.
 * Возвращает true, если сохранено.
 */
export const saveIfActive = (
  authSessionId: string,
  next: AuthSession,
): boolean => {
  if (loadAuthSession()?.authSessionId !== authSessionId) {
    return false;
  }
  saveAuthSession(next);
  return true;
};

export const updateAuthSession = (
  patch: Partial<AuthSession>,
): AuthSession | null => {
  const session = loadAuthSession();
  if (!session) {
    return null;
  }

  const next = { ...session, ...patch };
  saveAuthSession(next);
  return next;
};

export const clearAuthSession = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const createAuthSession = (params: {
  phone: number;
  authSessionId: string;
  expiresAt: string;
  channels: AuthChannel[];
  previous?: AuthSession | null;
}): AuthSession => {
  const { previous, ...rest } = params;
  const same =
    previous && previous.authSessionId === params.authSessionId
      ? previous
      : null;

  return {
    whiteLabelId: getWhiteLabelId(),
    apiContext: getApiContext(),
    deliveries: same?.deliveries ?? [],
    // Интервал отправки считается на телефон, в том числе между сессиями.
    nextSendAt:
      previous?.phone === params.phone ? previous.nextSendAt : undefined,
    pendingRequest: same?.pendingRequest,
    ...rest,
  };
};

/** Добавляет или обновляет отправку по request_id. */
export const upsertDelivery = (
  deliveries: Delivery[],
  delivery: Delivery,
): Delivery[] => {
  const index = deliveries.findIndex(
    (item) => item.request_id === delivery.request_id,
  );
  if (index === -1) {
    return [...deliveries, delivery];
  }

  const next = [...deliveries];
  next[index] = delivery;
  return next;
};

export const isSessionExpired = (session: AuthSession) =>
  isExpired(session.expiresAt);

export const FAILED_STATUSES: DeliveryStatus[] = ["rejected", "failed"];

/** Новый request_id для новой отправки. */
export const createRequestId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

/** Телефон из маски +7 (999) 999 99 99 → 79995637536. */
export const normalizePhone = (value: string): number | null => {
  const digits = value.replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("7") ? Number(digits) : null;
};

/** 79995637536 → +7 (999) 563 75 36 */
export const formatPhone = (phone: number | string): string => {
  const d = String(phone);
  return `+${d[0]} (${d.slice(1, 4)}) ${d.slice(4, 7)} ${d.slice(
    7,
    9,
  )} ${d.slice(9, 11)}`;
};
